// frontend/src/pages/inventaire/ConfigurationFidelite.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  LinearProgress
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Loyalty as LoyaltyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  EmojiEvents as TrophyIcon,
  Percent as PercentIcon,
  PointOfSale as PointIcon,
  AutoAwesome as AutoIcon,
  Calculate as CalculateIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  People as PeopleIcon,
  CardGiftcard as GiftIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/auth';
import { formatCurrency } from '../../../src/components/utils/formatters';

// ✅ FIX : Extraire le message d'erreur quelle que soit la structure backend
const extractErrorMessage = (err) => {
  const data = err.response?.data;
  if (!data) return 'Erreur lors de la sauvegarde';

  // Tableau direct : ["message"]
  if (Array.isArray(data)) return data.join(' ');

  // Objet avec clé non_field_errors : { non_field_errors: ["..."] }
  if (Array.isArray(data.non_field_errors)) return data.non_field_errors.join(' ');

  // Objet avec clé detail : { detail: "..." }
  if (data.detail) return data.detail;

  // Objet avec clé message : { message: "..." }
  if (data.message) return data.message;

  // Objet avec clé error : { error: "..." }
  if (data.error) return data.error;

  // Objet générique : { champ: ["erreur"] }
  const firstKey = Object.keys(data)[0];
  if (firstKey) {
    const val = data[firstKey];
    return Array.isArray(val) ? val.join(' ') : String(val);
  }

  return 'Erreur lors de la sauvegarde';
};

const ConfigurationFidelite = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ✅ FIX : Stocker l'id de la config pour les updates
  const [configId, setConfigId] = useState(null);

  // Configuration
  const [config, setConfig] = useState({
    est_actif: false,
    montant_par_point: 100,
    seuils_recompense: [],
    application_auto: false
  });

  // États pour les seuils
  const [openSeuilDialog, setOpenSeuilDialog] = useState(false);
  const [editingSeuil, setEditingSeuil] = useState(null);
  const [seuilForm, setSeuilForm] = useState({
    points: '',
    remise_pourcentage: ''
  });
  const [seuilErrors, setSeuilErrors] = useState({});

  // Simulation
  const [montantSimulation, setMontantSimulation] = useState('');
  const [pointsSimulation, setPointsSimulation] = useState(0);
  const [remiseSimulation, setRemiseSimulation] = useState(null);

  // Statistiques enrichies
  const [stats, setStats] = useState({
    totalClients: 0,
    clientsActifs: 0,
    pointsDistribues: 0,
    pointsUtilises: 0,
    remisesAccordees: 0,
    montantRemises: 0,
    tauxUtilisation: 0
  });

  // Chargement de la configuration
  useEffect(() => {
    fetchConfiguration();
    fetchStats();
  }, []);

  // Simulation des points
  useEffect(() => {
    if (montantSimulation && config.est_actif) {
      const montant = parseFloat(montantSimulation) || 0;
      const points = Math.floor(montant / (parseFloat(config.montant_par_point) || 100));
      setPointsSimulation(points);

      // Trouver la remise applicable
      const applicable = config.seuils_recompense
        .filter(s => points >= s.points)
        .sort((a, b) => b.points - a.points);
      setRemiseSimulation(applicable[0] || null);
    } else {
      setPointsSimulation(0);
      setRemiseSimulation(null);
    }
  }, [montantSimulation, config]);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await api.get('inventory/config-fidelite/');

      // ✅ FIX : L'API retourne { count, results: [...] }, prendre le premier résultat
      let data = response.data;
      if (data?.results && Array.isArray(data.results)) {
        data = data.results[0];
      }

      if (data) {
        setConfigId(data.id || null);
        setConfig({
          est_actif: data.est_actif || false,
          // ✅ FIX : parseFloat pour convertir "100.00" → 100
          montant_par_point: parseFloat(data.montant_par_point) || 100,
          seuils_recompense: Array.isArray(data.seuils_recompense)
            ? data.seuils_recompense.sort((a, b) => a.points - b.points)
            : [],
          application_auto: data.application_auto || false
        });
      }
    } catch (err) {
      console.error('Erreur chargement configuration:', err);
      if (err.response?.status !== 404) {
        setError('Impossible de charger la configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('inventory/fidelite/stats/');
      if (response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  const handleSaveConfiguration = async () => {
    if (!config.montant_par_point || parseFloat(config.montant_par_point) <= 0) {
      setError('Le montant par point doit être supérieur à 0');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        est_actif: config.est_actif,
        montant_par_point: parseFloat(config.montant_par_point),
        seuils_recompense: config.seuils_recompense,
        application_auto: config.application_auto
      };

      let response;
      // ✅ FIX : PUT si une config existe déjà, POST sinon
      if (configId) {
        response = await api.put(`inventory/config-fidelite/${configId}/`, payload);
      } else {
        response = await api.post('inventory/config-fidelite/', payload);
      }

      let data = response.data;
      if (data?.results && Array.isArray(data.results)) {
        data = data.results[0];
      }

      if (data) {
        setConfigId(data.id || configId);
        setConfig(prev => ({
          ...prev,
          est_actif: data.est_actif ?? prev.est_actif,
          montant_par_point: parseFloat(data.montant_par_point) || prev.montant_par_point,
          seuils_recompense: Array.isArray(data.seuils_recompense)
            ? data.seuils_recompense.sort((a, b) => a.points - b.points)
            : prev.seuils_recompense,
          application_auto: data.application_auto ?? prev.application_auto,
        }));
      }

      setSuccess('Configuration enregistrée avec succès');
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      // ✅ FIX : extractErrorMessage gère tous les formats d'erreur backend
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // Gestion des seuils
  const handleOpenSeuilDialog = (seuil = null) => {
    if (seuil) {
      setEditingSeuil(seuil);
      setSeuilForm({
        points: seuil.points,
        remise_pourcentage: seuil.remise_pourcentage
      });
    } else {
      setEditingSeuil(null);
      setSeuilForm({
        points: '',
        remise_pourcentage: ''
      });
    }
    setSeuilErrors({});
    setOpenSeuilDialog(true);
  };

  const validateSeuil = () => {
    const errors = {};

    if (!seuilForm.points || seuilForm.points <= 0) {
      errors.points = 'Points > 0';
    }

    if (!seuilForm.remise_pourcentage || seuilForm.remise_pourcentage <= 0) {
      errors.remise_pourcentage = '% > 0';
    } else if (seuilForm.remise_pourcentage > 100) {
      errors.remise_pourcentage = 'Max 100%';
    }

    if (!editingSeuil) {
      const exists = config.seuils_recompense.some(
        s => s.points === parseInt(seuilForm.points)
      );
      if (exists) {
        errors.points = 'Seuil déjà existant';
      }
    }

    setSeuilErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveSeuil = () => {
    if (!validateSeuil()) return;

    const newSeuil = {
      points: parseInt(seuilForm.points),
      remise_pourcentage: parseFloat(seuilForm.remise_pourcentage)
    };

    setConfig(prev => {
      let newSeuils = editingSeuil
        ? prev.seuils_recompense.map(s =>
            s.points === editingSeuil.points ? newSeuil : s
          )
        : [...prev.seuils_recompense, newSeuil];

      return {
        ...prev,
        seuils_recompense: newSeuils.sort((a, b) => a.points - b.points)
      };
    });

    setOpenSeuilDialog(false);
  };

  const handleDeleteSeuil = (points) => {
    setConfig(prev => ({
      ...prev,
      seuils_recompense: prev.seuils_recompense.filter(s => s.points !== points)
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      {/* Header compact */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
            <LoyaltyIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6">Programme de fidélité</Typography>
            <Typography variant="caption" color="textSecondary">
              Configurez points et récompenses
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => navigate('/scenes/Configuration')}>
            Annuler
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveConfiguration}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? '...' : 'Enregistrer'}
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* Ligne 1: Activation (1/3) + Statistiques (2/3) */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SettingsIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Activation</Typography>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">État du programme</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={config.est_actif}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        est_actif: e.target.checked
                      }))}
                      color="warning"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {config.est_actif ? 'Activé' : 'Désactivé'}
                    </Typography>
                  }
                  labelPlacement="start"
                />
              </Box>

              {config.est_actif && (
                <Alert severity="success" sx={{ mt: 1, py: 0.5 }}>
                  <Typography variant="caption">Programme actif et opérationnel</Typography>
                </Alert>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SpeedIcon fontSize="small" color="info" />
              <Typography variant="subtitle2">Statistiques du programme</Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <PeopleIcon color="primary" sx={{ fontSize: 28, mb: 0.5 }} />
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {stats.totalClients}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Clients inscrits
                  </Typography>
                  <Typography variant="caption" display="block" color="success.main">
                    {stats.clientsActifs} actifs
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <GiftIcon color="warning" sx={{ fontSize: 28, mb: 0.5 }} />
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {stats.pointsDistribues}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Points distribués
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {stats.pointsUtilises} utilisés
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <PercentIcon color="success" sx={{ fontSize: 28, mb: 0.5 }} />
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {stats.remisesAccordees}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Remises accordées
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {formatCurrency(stats.montantRemises)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <TrendingIcon color="info" sx={{ fontSize: 28, mb: 0.5 }} />
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {stats.tauxUtilisation}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Taux d'utilisation
                  </Typography>
                  <Box sx={{ mt: 0.5, px: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={stats.tauxUtilisation}
                      color="warning"
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Ligne 2: Règles d'accumulation + Application auto */}
        <Grid item xs={12} sm={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <PointIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2">Taux de conversion</Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              // ✅ FIX : String() pour éviter l'affichage de "0" avec une valeur numérique
              value={config.montant_par_point === 0 ? '' : String(config.montant_par_point)}
              onChange={(e) => setConfig(prev => ({
                ...prev,
                montant_par_point: e.target.value === '' ? '' : parseFloat(e.target.value) || 0
              }))}
              disabled={!config.est_actif}
              InputProps={{
                startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                endAdornment: <InputAdornment position="end">= 1 point</InputAdornment>
              }}
              type="number"
              inputProps={{ min: 1, step: 1 }}
            />

            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Ex: {formatCurrency(parseFloat(config.montant_par_point) || 0)} d'achat = 1 point
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <AutoIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2">Application automatique</Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={config.application_auto}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    application_auto: e.target.checked
                  }))}
                  disabled={!config.est_actif}
                />
              }
              label={<Typography variant="body2">Appliquer meilleure remise en caisse</Typography>}
            />

            {config.application_auto && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                ✓ La remise sera appliquée automatiquement
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Ligne 3: Seuils (pleine largeur) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrophyIcon fontSize="small" color="warning" />
                <Typography variant="subtitle2">Seuils de récompense</Typography>
                {config.seuils_recompense.length > 0 && (
                  <Chip
                    size="small"
                    label={config.seuils_recompense.length}
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>

              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenSeuilDialog()}
                disabled={!config.est_actif}
              >
                Ajouter un seuil
              </Button>
            </Box>

            {config.seuils_recompense.length === 0 ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="caption">Aucun seuil de récompense défini</Typography>
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Points requis</TableCell>
                      <TableCell>Remise</TableCell>
                      <TableCell>Montant nécessaire</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {config.seuils_recompense.map((seuil) => (
                      <TableRow key={seuil.points} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={<LoyaltyIcon />}
                            label={`${seuil.points}`}
                            color="warning"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={<PercentIcon />}
                            label={`${seuil.remise_pourcentage}%`}
                            color="success"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(seuil.points * parseFloat(config.montant_par_point))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenSeuilDialog(seuil)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteSeuil(seuil.points)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Ligne 4: Simulateur + Impact (si actif) */}
        {config.est_actif && config.seuils_recompense.length > 0 && (
          <>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CalculateIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">Simulateur</Typography>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Montant d'achat"
                  value={montantSimulation}
                  onChange={(e) => setMontantSimulation(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">HTG</InputAdornment>
                  }}
                  sx={{ mb: 1.5 }}
                />

                {montantSimulation && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        icon={<LoyaltyIcon />}
                        label={`${pointsSimulation} points`}
                        color="warning"
                      />
                      {remiseSimulation && (
                        <Chip
                          size="small"
                          icon={<PercentIcon />}
                          label={`${remiseSimulation.remise_pourcentage}% de remise`}
                          color="success"
                        />
                      )}
                    </Box>
                    {remiseSimulation && (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                        Seuil des {remiseSimulation.points} points atteint
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <TimelineIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">Impact du programme</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                  <Box textAlign="center">
                    <Typography variant="body2" color="textSecondary">Premier palier</Typography>
                    <Typography variant="h6" color="success.main">
                      {config.seuils_recompense[0]?.remise_pourcentage}%
                    </Typography>
                    <Typography variant="caption">
                      {config.seuils_recompense[0]?.points} pts
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ height: 40 }} />
                  <Box textAlign="center">
                    <Typography variant="body2" color="textSecondary">Dernier palier</Typography>
                    <Typography variant="h6" color="warning.main">
                      {config.seuils_recompense[config.seuils_recompense.length - 1]?.remise_pourcentage}%
                    </Typography>
                    <Typography variant="caption">
                      {config.seuils_recompense[config.seuils_recompense.length - 1]?.points} pts
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ height: 40 }} />
                  <Box textAlign="center">
                    <Typography variant="body2" color="textSecondary">Nombre</Typography>
                    <Typography variant="h6">
                      {config.seuils_recompense.length}
                    </Typography>
                    <Typography variant="caption">
                      paliers
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </>
        )}
      </Grid>

      {/* Dialog Ajout/Modification seuil */}
      <Dialog open={openSeuilDialog} onClose={() => setOpenSeuilDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1.5 }}>
          {editingSeuil ? 'Modifier' : 'Ajouter'} un seuil
        </DialogTitle>
        <DialogContent dividers sx={{ py: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Points requis"
                value={seuilForm.points}
                onChange={(e) => setSeuilForm(prev => ({ ...prev, points: e.target.value }))}
                error={!!seuilErrors.points}
                helperText={seuilErrors.points}
                type="number"
                InputProps={{ endAdornment: <InputAdornment position="end">pts</InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Remise"
                value={seuilForm.remise_pourcentage}
                onChange={(e) => setSeuilForm(prev => ({ ...prev, remise_pourcentage: e.target.value }))}
                error={!!seuilErrors.remise_pourcentage}
                helperText={seuilErrors.remise_pourcentage}
                type="number"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>

            {seuilForm.points && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0 }}>
                  <Typography variant="caption">
                    Achat minimum: {formatCurrency(seuilForm.points * parseFloat(config.montant_par_point))}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ py: 1, px: 2 }}>
          <Button size="small" onClick={() => setOpenSeuilDialog(false)}>Annuler</Button>
          <Button size="small" variant="contained" onClick={handleSaveSeuil}>
            {editingSeuil ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ py: 0 }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ py: 0 }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigurationFidelite;
